// features/workstations/shared/validation-toggle.js
// ═══════════════════════════════════════════════════════════
// VALIDATION TOGGLE — the user's stance on an AI-proposed
// item. Holds / Breaking / Invalidated, with annotation.
// Annotations are append-only (never overwrite history).
//
// Used by Findings Pattern Library; will be used by Learn
// (Helpful / Off / Already-known), Knowledge Feed (Relevant /
// Not-relevant), and future Discoveries.
//
// Props:
//   currentState     - 'pending' | 'holds' | 'breaking' | 'invalidated'
//                      (or whatever values caller passes — see `states`)
//   states           - array of { key, label, tone } — defaults to
//                      Holds/Breaking/Invalidated
//   lastValidatedAt  - ISO string, or null if never validated
//   lastNote         - { note, validated_at } most recent annotated entry
//   totalValidations - integer count of validations to show "X total"
//   busy             - disable while in-flight
//   onChange(stateKey, note) - async, returns a promise
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

const DEFAULT_STATES = [
  { key: 'holds',        label: '\u2713 Still Holds',   tone: 'sage'  },
  { key: 'breaking',     label: '\u26A1 Breaking Down', tone: 'gold'  },
  { key: 'invalidated',  label: '\u2715 Invalidated',   tone: 'terra' },
];

export default function ValidationToggle({
  currentState,
  states = DEFAULT_STATES,
  lastValidatedAt,
  lastNote,
  totalValidations,
  busy = false,
  onChange,
  label = 'Your Validation \u00B7 Is this still holding?',
}) {
  const [annotationDraft, setAnnotationDraft] = useState('');
  const [showAnnotation, setShowAnnotation] = useState(false);

  async function pick(key) {
    if (busy || !onChange) return;
    const note = key === currentState ? '' : annotationDraft.trim();
    try {
      await onChange(key, note || null);
      setAnnotationDraft('');
      setShowAnnotation(false);
    } catch {
      /* parent handles error */
    }
  }

  return (
    <div className="ws-validation-block">
      <div className="ws-validation-label">{label}</div>
      <div className="ws-validation-row">
        <div className="ws-val-toggle">
          {states.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`ws-vt-btn is-${s.key}${currentState === s.key ? ' on' : ''}`}
              onClick={() => pick(s.key)}
              disabled={busy}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="ws-validation-date">
          {lastValidatedAt
            ? `Last validated ${formatDate(lastValidatedAt)}`
            : 'Not yet validated'}
        </div>
      </div>

      {lastNote?.note && (
        <div className="ws-annotation-saved">
          <em>"{lastNote.note}"</em>
          <div className="ws-annotation-meta">
            Note added {formatDate(lastNote.validated_at)}
            {totalValidations > 1 && ` \u00B7 ${totalValidations} validations total`}
          </div>
        </div>
      )}

      {!showAnnotation ? (
        <button
          type="button"
          className="ws-annotation-add"
          onClick={() => setShowAnnotation(true)}
        >
          + Add observation
        </button>
      ) : (
        <div className="ws-annotation-form">
          <input
            className="ws-annotation-input"
            value={annotationDraft}
            onChange={(e) => setAnnotationDraft(e.target.value)}
            placeholder="What do you observe today? (saved alongside the next state you pick)"
            autoFocus
          />
          <div className="ws-annotation-form-hint">
            Pick a state above to save this annotation alongside it.
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}
